import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store";
import { Tab, Nav, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaBrain } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";
import { ChatbotSettings } from "../types";
import { useChatbotTraining } from "../hooks/useChatbotTraining";
import ProfileSettings from "../training/ProfileSettings";
import KnowledgeSources from "../training/KnowledgeSources";
import SearchPreview from "../training/SearchPreview";
import { RiChatVoiceAiLine } from "react-icons/ri";

interface TrainingTabProps {
  chatbot: any;
  settingsData: ChatbotSettings;
  setSettingsData: React.Dispatch<React.SetStateAction<ChatbotSettings>>;
  isEditMode: boolean;
}

const TrainingTab: React.FC<TrainingTabProps> = ({
  chatbot,
  settingsData,
  setSettingsData,
  isEditMode,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [activeSubTab, setActiveSubTab] = useState("profile");
  const [sourcesLoaded, setSourcesLoaded] = useState(false);

  const canSeeSearchPreview = user?.roles?.some(role => ["admin", "super_admin"].includes(role));

  const subTabs = [
    { key: "profile", label: "Assistant Profile & Guardrails" },
    { key: "sources", label: "Sources" },
    ...(canSeeSearchPreview ? [{ key: "search", label: "Search Preview" }] : []),
  ];

  const {
    trainingLoading,
    trainingError,
    sources,
    setSources,
    sourceStatsMap,
    selectedSourceId,
    setSelectedSourceId,
    newSourceTitle,
    setNewSourceTitle,
    newSourceLang,
    setNewSourceLang,
    newText,
    setNewText,
    contentType,
    setContentType,
    fileUploading,
    searchQ,
    setSearchQ,
    searchResults,
    jobs,
    polling,
    handleCreateSource,
    handleIngestText,
    handleFileUpload,
    handleDeleteSource,
    handleSearchPreview,
    refreshSources,
  } = useChatbotTraining(chatbot?.id, setSettingsData);

  // Load sources when Sources tab becomes active
  useEffect(() => {
    if (activeSubTab === "sources" && chatbot?.id && !sourcesLoaded) {
      refreshSources();
      setSourcesLoaded(true);
    }
  }, [activeSubTab, chatbot?.id, sourcesLoaded, refreshSources]);

  return (
    <div className="chatbots-edit-tab-content">

      {trainingLoading && (
        <div className="mb-3 text-muted">Loading training data...</div>
      )}
      {trainingError && <Alert variant="danger">{trainingError}</Alert>}

      <Tab.Container
        activeKey={activeSubTab}
        onSelect={(k) => setActiveSubTab(k || "profile")}
      >
        <Nav variant="pills" className="chatbots-double-tabs">
          {subTabs.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                eventKey={tab.key}
                className="chatbots-double-tabs-link"
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="profile" className="training-sub-pane">
            <ProfileSettings
              settingsData={settingsData}
              setSettingsData={setSettingsData}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="sources" className="training-sub-pane">
            <KnowledgeSources
              chatbotId={chatbot?.id}
              sources={sources}
              setSources={setSources}
              sourceStatsMap={sourceStatsMap}
              selectedSourceId={selectedSourceId}
              setSelectedSourceId={setSelectedSourceId}
              newSourceTitle={newSourceTitle}
              setNewSourceTitle={setNewSourceTitle}
              newSourceLang={newSourceLang}
              setNewSourceLang={setNewSourceLang}
              newText={newText}
              setNewText={setNewText}
              contentType={contentType}
              setContentType={setContentType}
              fileUploading={fileUploading}
              jobs={jobs}
              polling={polling}
              handleCreateSource={handleCreateSource}
              handleIngestText={handleIngestText}
              handleFileUpload={handleFileUpload}
              handleDeleteSource={handleDeleteSource}
              refreshSources={refreshSources}
              isEditMode={isEditMode}
            />
          </Tab.Pane>
          {canSeeSearchPreview && (
            <Tab.Pane eventKey="search">
              <SearchPreview
                searchQ={searchQ}
                setSearchQ={setSearchQ}
                searchResults={searchResults}
                handleSearchPreview={handleSearchPreview}
              />
            </Tab.Pane>
          )}
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default TrainingTab;



